// The conditions row of a card (v2 slot 1; ui-map-interface.md §8 "The
// conditions row"; mockup round 088 card 02 a).
//
// A modifier of v2 (`v2-the-deep.md` §6) changes one figure of one fleet,
// and the card must show it. The row stands between the vitals and the
// facts: one chip per stack key, in the order the list gives them, each
// with a tone (bad, good or neutral) and an icon, so the row reads in
// greyscale too. Nothing fills the row until the `modifiers` phase of v2
// sends the list; an empty list draws nothing, and the card is the card
// of round 087.

import { esc } from '../esc';

/** The tone of one condition. */
export type CondTone = 'bad' | 'good' | 'neutral';

/** One condition: one stack key of the modifier list. */
export interface Cond {
  /** The stack key, which keeps the chip one per key. */
  key: string;
  /** The cap word, for example SLOWED. */
  label: string;
  /** The figure, for example `×0.6 · 8m`. */
  value?: string;
  tone: CondTone;
  /** The glyph of the chip; the tone's own glyph when absent. */
  icon?: string;
  /** The sentence of its cause, as the chip's title. */
  why?: string;
  /** The condition costs hull, which makes it the worst of the bad ones. */
  hull?: boolean;
}

/** The glyph of each tone: a shape, so a reader without colour reads it. */
export const COND_GLYPH: Readonly<Record<CondTone, string>> = {
  bad: '▼',
  good: '▲',
  neutral: '◆',
};

/** One chip per stack key, the first chip of a key wins. */
export function condsHtml(list: readonly Cond[] | undefined): string {
  if (!list || list.length === 0) return '';
  const seen = new Set<string>();
  let html = '';
  for (const c of list) {
    if (seen.has(c.key)) continue;
    seen.add(c.key);
    html += `<span class="ck-cond ${c.tone}"${c.why ? ` title="${esc(c.why)}"` : ''}>`
      + `<i aria-hidden="true">${esc(c.icon ?? COND_GLYPH[c.tone])}</i><b>${esc(c.label)}</b>`
      + (c.value ? `<em>${esc(c.value)}</em>` : '') + '</span>';
  }
  return `<div class="ck-conds">${html}</div>`;
}

/**
 * The one condition the telephone peek line shows in place of the class
 * line (round 088 card 02): a bad condition that costs hull first, else
 * the first bad condition in the order of the list. A good or a neutral
 * condition never takes the line, so null keeps the class line.
 */
export function worstCondition(list: readonly Cond[] | undefined): Cond | null {
  const bad = (list ?? []).filter((c) => c.tone === 'bad');
  return bad.find((c) => c.hull) ?? bad[0] ?? null;
}
